import { Component, OnInit } from '@angular/core';
import { AlertController, PopoverController } from '@ionic/angular';
import * as _ from 'lodash';

import {
  ActionDropdownType,
  ActionDropdownComponent,
} from 'src/app/components/action-dropdown/action-dropdown.component';
import { Organizer } from 'src/models';
import { TENANT_USER_ROLES, TenantUserRole } from 'src/models/users';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { UsersService } from 'src/services/users.service';

@Component({
  selector: 'app-admin-account',
  templateUrl: './admin-account.component.html',
  styleUrls: ['./admin-account.component.scss'],
})
export class AdminAccountComponent implements OnInit {
  initialized = false;
  name: string;
  customFields: any[] = [];
  slackEnabled: boolean = false;
  defaultFieldSettings: { [key: string]: { public?: boolean } }[];
  seatsUsed = 0;
  defaultFields = [
    {
      id: 'pronoun',
      title: 'Pronouns',
    },
    {
      id: 'title',
      title: 'Title',
    },
    {
      id: 'department',
      title: 'Department',
    },
    {
      id: 'organization',
      title: 'Organization',
    },
    {
      id: 'division',
      title: 'Division',
    },
    {
      id: 'costCenter',
      title: 'Cost Center',
    },
    {
      id: 'employeeNumber',
      title: 'Employee ID',
    },
    {
      id: 'streetAddress',
      title: 'Street Address',
    },
    {
      id: 'locality',
      title: 'City',
    },
    {
      id: 'region',
      title: 'Region',
    },
    {
      id: 'postalCode',
      title: 'Postal Code',
    },
    {
      id: 'country',
      title: 'Country',
    },
  ];
  isLoadingCheckout = false;

  // New properties for managing owners and admins
  owners: any[] = [];
  admins: any[] = [];
  isLoadingUsers = false;
  TENANT_USER_ROLES = TENANT_USER_ROLES;

  constructor(
    private msgSrvc: MessageService,
    public authService: AuthService,
    private apiService: ApiService,
    private popoverCtrl: PopoverController,
    private usersService: UsersService,
  ) {}

  async ngOnInit() {
    await this.load();
  }

  async load() {
    const tenant = this.authService.tenant;

    this.slackEnabled = this.authService.integrations.slackEnabled;
    this.name = tenant?.name;

    this.customFields = _.get(tenant, 'users.customProfileFields', []);
    this.defaultFieldSettings = _.defaults(
      _.get(tenant, 'defaultFieldSettings', {}),
      this.defaultFields.reduce((acc, f) => ({ ...acc, [f.id]: {} }), {}),
    );

    if (tenant?.seatsUsed) {
      this.seatsUsed = tenant.seatsUsed;
    }

    // Load owners and admins
    await this.loadOwnersAndAdmins();

    this.initialized = true;
  }

  async loadOwnersAndAdmins() {
    if (!this.authService.isTenantOwner) {
      return;
    }

    this.isLoadingUsers = true;
    try {
      const users = await this.usersService.list({
        limit: null,
        order: ['name'],
        descending: false,
        where: [
          {
            field: 'role',
            operator: 'in',
            value: [TENANT_USER_ROLES.OWNER, TENANT_USER_ROLES.ADMIN],
          },
        ],
      });

      this.owners = users.data.filter((user) => user.role === TENANT_USER_ROLES.OWNER);
      this.admins = users.data.filter((user) => user.role === TENANT_USER_ROLES.ADMIN);
    } catch (error) {
      console.error('Error loading owners and admins:', error);
      this.msgSrvc.showBottom('Error loading users');
    } finally {
      this.isLoadingUsers = false;
    }
  }

  async changeRole(userId: string, currentRole: string) {
    const options: ActionDropdownType[] = [
      {
        label: 'Owner',
        id: TENANT_USER_ROLES.OWNER,
        icon: 'shield-checkmark-outline',
      },
      {
        label: 'Admin',
        id: TENANT_USER_ROLES.ADMIN,
        icon: 'shield-outline',
      },
      {
        label: 'Standard',
        id: TENANT_USER_ROLES.STANDARD,
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (i) => {
          popover.dismiss();

          const newRole = options[i].id as TenantUserRole;

          try {
            await this.usersService.updateUser(userId, { role: newRole });
            this.msgSrvc.showBottom('Role updated successfully');
            await this.loadOwnersAndAdmins();
          } catch (error) {
            this.msgSrvc.showBottom('Error updating role');
          }
        },
        onCloseCallback() {
          popover.dismiss();
        },
      },
      showBackdrop: true,
      backdropDismiss: true,
      keyboardClose: true,
    });

    popover.present();
  }

  async save() {
    if (!this.initialized || !this.name) {
      return;
    }

    // const updates: any = {
    //   defaultCommunityId: this.defaultCommunityId,
    //   defaultFieldSettings: this.defaultFieldSettings,
    //   permissions: {
    //     canCreateOrganizers: this.canCreateOrganizers,
    //     canViewPeople: this.canViewPeople,
    //   },
    //   users: {
    //     customProfileFields: this.customFields?.map((f) => {
    //       if (!f.id) {
    //         f.id = _.camelCase(f.title);
    //       }

    //       return f;
    //     }),
    //     phoneNumberRequirement: this.phoneNumberRequirement,
    //   },
    // };

    const updates = {
      name: this.name,
    };

    this.initialized = false;

    try {
      await this.authService.updateTenant(updates);

      this.load();
      this.msgSrvc.showBottom('✔︎ Saved');
    } catch (err) {
      this.msgSrvc.showBottom(`Whoops! An error occurred: ${err.message}`);
      this.initialized = true;
    }
  }

  async manageSubscription() {
    this.isLoadingCheckout = true;
    try {
      const session: any = await this.apiService.postPromise('/stripe/generate-billing-session', {});
      window.open(session.url, '_blank');
      this.isLoadingCheckout = false;
    } catch (error) {
      this.isLoadingCheckout = false;
    }
  }

  async showSubscriptionOptions() {
    const CANCEL_SUBSCRIPTION = 'Cancel subscription';
    const ACCOUNT_DELETION = 'Request account deletion';
    const CONTACT_SALES = 'Contact sales';

    const options: ActionDropdownType[] = [
      {
        label: CONTACT_SALES,
      },
    ];

    if (this.authService.accountStatus === 'active') {
      options.push({
        label: CANCEL_SUBSCRIPTION,
      });
    }

    options.push({
      label: ACCOUNT_DELETION,
    });

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (_idx: number, label: string) => {
          popover.dismiss();

          if (label === CONTACT_SALES) {
            window.open(`mailto:sales@unthread.io`, '_blank');
          } else if (label === CANCEL_SUBSCRIPTION) {
            this.manageSubscription();
          } else if (label === ACCOUNT_DELETION) {
            window.open(
              `mailto:support@unthread.io?subject=Account+Deletion+Request+${this.authService.tenantId}`,
              '_blank',
            );
          }
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }
}
